import React, { useState, useRef } from 'react';
import './AdminPanel.css';
import API_URL, { parseJsonResponse } from '../api';
import { matchesShiftFilter } from '../utils/dbHelpers';
import { ShiftScheduleOptions, getShiftLabel } from '../constants/shiftOptions';
import { PositionOptions } from '../constants/positionOptions';
import EmployeeQRCode from './EmployeeQRCode';
import EmployeeQRModal from './EmployeeQRModal';

const EmployeeList = ({
  searchDept, setSearchDept, filterShift, setFilterShift,
  registeredEmployees, editingId, setEditingId,
  editName, setEditName, editPosition, setEditPosition,
  editShift, setEditShift,
  handleUpdateEmployee, startEdit, handleDeleteEmployee,
  isAdmin = false,
  canManageActions = false,
  onRefreshEmployees,
}) => {

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [qrModalEmployee, setQrModalEmployee] = useState(null);
  const [isEditingModal, setIsEditingModal] = useState(false);
  const [modalRemarks, setModalRemarks] = useState('');
  const [modalMOA, setModalMOA] = useState('');
  const [photoUploadTargetId, setPhotoUploadTargetId] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const photoInputRef = useRef(null);
  const editSelectClass = 'admin-form-input admin-table-select';

  const handlePhotoClick = (emp) => {
    if (!canManageActions || isUploadingPhoto) return;
    setPhotoUploadTargetId(emp.employeeId);
    photoInputRef.current?.click();
  };

  const handlePhotoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !photoUploadTargetId) return;

    if (!file.type.startsWith('image/')) {
      alert('Pili lang ng image file.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const photoBase64 = reader.result;
      setIsUploadingPhoto(true);
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'update_employee_photo',
            employeeId: photoUploadTargetId,
            photo: photoBase64,
          }),
        });
        const data = await parseJsonResponse(response);
        if (data?.status === 'success') {
          if (selectedAgent?.employeeId === photoUploadTargetId) {
            setSelectedAgent((prev) => (prev ? { ...prev, photo: photoBase64 } : prev));
          }
          if (onRefreshEmployees) await onRefreshEmployees();
          alert('Na-update na ang litrato.');
        } else {
          alert(data.message || 'Hindi na-update ang litrato.');
        }
      } catch (error) {
        console.error('Photo update error:', error);
        alert('Server error. Subukan ulit.');
      } finally {
        setIsUploadingPhoto(false);
        setPhotoUploadTargetId(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setModalMOA(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveAgentDetails = async () => {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_agent_details',
          employeeId: selectedAgent.employeeId,
          remarks: modalRemarks,
          moa_url: modalMOA
        })
      });
      const data = await parseJsonResponse(response);
      if (data?.status === 'success') {
        alert("Details updated successfully!");
        setIsEditingModal(false);
        setIsModalOpen(false);
        window.location.reload(); 
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Save error:", error);
    }
  };

  const getStatusClass = (status) => {
    if (status === 'LATE') return 'admin-pill admin-pill--late';
    if (status === 'UNDERTIME') return 'admin-pill admin-pill--undertime';
    if (status === 'OFF DUTY') return 'admin-pill admin-pill--off';
    if (status === 'ACTIVE') return 'admin-pill admin-pill--active';
    return 'admin-pill admin-pill--inactive';
  };

  const filteredCount = registeredEmployees
    .filter(emp => emp.position.toLowerCase().includes(searchDept.toLowerCase()) || emp.name.toLowerCase().includes(searchDept.toLowerCase()))
    .filter(emp => matchesShiftFilter(emp.shift, filterShift)).length;

  return (
    <div className="admin-panel admin-panel--wide">
      <div className="admin-panel-header">
        <div className="admin-panel-header-left">
          <div className="admin-panel-badge">
            <span className="admin-panel-badge-dot" />
            Workforce
          </div>
          <h2 className="admin-panel-title">Employee List</h2>
          <p className="admin-panel-subtitle">{filteredCount} employee{filteredCount !== 1 ? 's' : ''} found</p>
        </div>

        <div className="admin-toolbar">
          <div className="admin-search-box">
            <span className="admin-search-label">Search</span>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Dept or Name..."
              value={searchDept}
              onChange={(e) => setSearchDept(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <button
              onClick={() => setFilterShift('Day Shift')}
              className={`admin-filter-btn ${filterShift === 'Day Shift' ? 'active' : ''}`}
            >Day</button>
            <button
              onClick={() => setFilterShift('Night Shift')}
              className={`admin-filter-btn ${filterShift === 'Night Shift' ? 'active' : ''}`}
            >Night</button>
            <button
              onClick={() => setFilterShift('')}
              className={`admin-filter-btn ${filterShift === '' ? 'active-neutral' : ''}`}
            >All</button>
          </div>
        </div>
      </div>

      <div className="admin-table-wrap">
      <input
        type="file"
        ref={photoInputRef}
        accept="image/*"
        onChange={handlePhotoFileChange}
        style={{ display: 'none' }}
      />
      <table className="admin-table">
        <thead>
          <tr>
            <th>Photo</th>
            <th>EID</th>
            <th>UID</th>
            <th>Name</th>
            <th>Position</th>
            <th>Date Hired</th>
            <th>Shift</th>
            <th>Status</th>
            <th>QR</th>
            {canManageActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {registeredEmployees
            .filter(emp => emp.position.toLowerCase().includes(searchDept.toLowerCase()) || emp.name.toLowerCase().includes(searchDept.toLowerCase()))
            .filter(emp => matchesShiftFilter(emp.shift, filterShift))
            .map((emp) => {
              const isEditing = editingId === emp.employeeId;
              return (
                <tr key={emp.employeeId}>
                  <td>
                    <button
                      type="button"
                      className={`admin-photo-change-btn ${canManageActions ? 'admin-photo-change-btn--editable' : ''}`}
                      onClick={() => handlePhotoClick(emp)}
                      disabled={!canManageActions || isUploadingPhoto}
                      title={canManageActions ? 'Update your Photo' : undefined}
                    >
                      <img src={emp.photo} alt="" className="admin-table-photo" />
                      {canManageActions && (
                        <span className="admin-photo-change-overlay" aria-hidden="true">📷</span>
                      )}
                    </button>
                  </td>
                  <td><span className="admin-table-muted">{emp.eid || '—'}</span></td>
                  <td><span className="admin-table-muted">{emp.employeeId}</span></td>

                  <td>
                    {canManageActions && isEditing ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className={editSelectClass}
                      />
                    ) : (
                      <span
                        className="admin-table-name"
                        onClick={() => { setSelectedAgent(emp); setIsEditingModal(false); setIsModalOpen(true); }}
                      >
                        {emp.name}
                      </span>
                    )}
                  </td>

                  <td>
                    {canManageActions && isEditing ? (
                      <select value={editPosition} onChange={(e) => setEditPosition(e.target.value)} className={editSelectClass}>
                        <PositionOptions currentValue={editPosition} />
                      </select>
                    ) : (
                      <span className="admin-table-text">{emp.position}</span>
                    )}
                  </td>

                  <td><span className="admin-table-text">{emp.date_hired || '—'}</span></td>

                  <td>
                    {canManageActions && isEditing ? (
                      <select value={editShift} onChange={(e) => setEditShift(e.target.value)} className={editSelectClass}>
                        <ShiftScheduleOptions />
                      </select>
                    ) : (
                      <span className="admin-table-text">{getShiftLabel(emp.shift)}</span>
                    )}
                  </td>

                  <td>
                    <span className={getStatusClass(emp.status)}>{emp.status || 'INACTIVE'}</span>
                  </td>

                  <td>
                    <button
                      type="button"
                      className="admin-btn admin-btn--qr"
                      onClick={() => setQrModalEmployee(emp)}
                      title={`View QR code for ${emp.name}`}
                    >
                      QR
                    </button>
                  </td>

                  {canManageActions && (
                  <td className="admin-table-actions">
                    {isEditing ? (
                      <div className="admin-btn-group">
                        <button type="button" onClick={handleUpdateEmployee} className="admin-btn admin-btn--save">Save</button>
                        <button type="button" onClick={() => setEditingId(null)} className="admin-btn admin-btn--cancel">Cancel</button>
                      </div>
                    ) : (
                      <div className="admin-btn-group">
                        <button type="button" onClick={() => startEdit(emp)} className="admin-btn admin-btn--edit">Edit</button>
                        <button type="button" onClick={() => handleDeleteEmployee(emp.employeeId, emp.name)} className="admin-btn admin-btn--delete">Delete</button>
                      </div>
                    )}
                  </td>
                  )}
                </tr>
              );
            })}
        </tbody>
      </table>
      </div>

      <EmployeeQRModal
        employee={qrModalEmployee}
        onClose={() => setQrModalEmployee(null)}
      />

      {isModalOpen && selectedAgent && (
        <div className="admin-modal-overlay" onClick={() => { setIsModalOpen(false); setIsEditingModal(false); }}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="admin-modal-close"
              onClick={() => { setIsModalOpen(false); setIsEditingModal(false); }}
            >✕</button>

            <div className="admin-modal-scroll">
            <div className="admin-modal-header">
              <div className="admin-modal-photo-wrap">
                <button
                  type="button"
                  className={`admin-photo-change-btn admin-photo-change-btn--modal ${canManageActions ? 'admin-photo-change-btn--editable' : ''}`}
                  onClick={() => handlePhotoClick(selectedAgent)}
                  disabled={!canManageActions || isUploadingPhoto}
                  title={canManageActions ? 'Palitan ang litrato' : undefined}
                >
                  <img src={selectedAgent.photo} alt="" className="admin-modal-photo" />
                  {canManageActions && (
                    <span className="admin-photo-change-overlay admin-photo-change-overlay--modal" aria-hidden="true">📷</span>
                  )}
                </button>
              </div>
              <h3 className="admin-modal-name">{selectedAgent.name}</h3>
              <span className="admin-modal-position">{selectedAgent.position}</span>
            </div>

            <div className="admin-modal-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span className="admin-modal-label">Team Leader</span>
                  <div className="admin-modal-value">
                    {selectedAgent.tl_name ||
                      (selectedAgent.added_by && selectedAgent.added_by !== 'admin' ? selectedAgent.added_by : null) ||
                      'Unassigned TL'}
                  </div>
                </div>
                {isAdmin && !isEditingModal && (
                  <button
                    type="button"
                    className="admin-btn admin-btn--edit"
                    onClick={() => {
                      setIsEditingModal(true);
                      setModalRemarks(selectedAgent.remarks || '');
                      setModalMOA(selectedAgent.moa_url || '');
                    }}
                  >Edit Info</button>
                )}
              </div>
            </div>

            <div className="admin-modal-section">
              <span className="admin-modal-label">Memorandum of Agreement</span>
              {isEditingModal && isAdmin ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label htmlFor="moa-upload-input" className="admin-upload-btn" style={{ textAlign: 'center', cursor: 'pointer' }}>
                    {modalMOA ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  <input id="moa-upload-input" type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                  {modalMOA && (
                    <button type="button" className="admin-btn admin-btn--cancel" onClick={() => setModalMOA('')}>Remove Photo</button>
                  )}
                </div>
              ) : (
                <div className={`admin-modal-moa-box ${!selectedAgent.moa_url ? 'admin-modal-moa-box--empty' : ''}`}>
                  {selectedAgent.moa_url ? (
                    <img src={selectedAgent.moa_url} alt="MOA Document" style={{ width: '100%', maxHeight: 160, objectFit: 'contain' }} />
                  ) : (
                    <span className="admin-table-muted">No MOA image uploaded.</span>
                  )}
                </div>
              )}
            </div>

            <div className="admin-modal-section">
              <span className="admin-modal-label">Remarks / Notes</span>
              {isEditingModal && isAdmin ? (
                <textarea
                  className="admin-modal-textarea"
                  value={modalRemarks}
                  onChange={(e) => setModalRemarks(e.target.value)}
                  rows={3}
                />
              ) : (
                <div className="admin-modal-value" style={{ fontSize: '0.82rem', fontStyle: 'italic' }}>
                  {selectedAgent.remarks || 'No remarks provided.'}
                </div>
              )}
            </div>

            <div className="admin-modal-qr-wrap">
              <EmployeeQRCode employee={selectedAgent} />
            </div>

            {isEditingModal && isAdmin && (
              <div className="admin-modal-actions">
                <button type="button" onClick={handleSaveAgentDetails} className="admin-btn admin-btn--save" style={{ flex: 1 }}>Save Changes</button>
                <button type="button" onClick={() => setIsEditingModal(false)} className="admin-btn admin-btn--cancel" style={{ flex: 1 }}>Cancel</button>
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;